import { Badge, Button, Stack, ThemeIcon, Title } from "@mantine/core";
import { Phone, PhoneIncoming, PhoneOff, Video } from "lucide-react";

export interface IncomingCallCardDecline {
  loading: boolean;
  onDecline: () => void;
}

export interface IncomingCallCardProps {
  askingFor: string;
  /** Where the visitor is, e.g. "Main entrance, L26"; "" when unknown (show "reception"). */
  location: string;
  isTest: boolean;
  onAnswer: (video: boolean) => void;
  /** Mid-answer (acquiring the camera/microphone, or accepting): both buttons disabled, video shows a spinner. */
  answerBusy?: boolean;
  /** Only the popup offers this — the call page has no decline flow of its own. */
  decline?: IncomingCallCardDecline;
  /** Ties the title to an `aria-labelledby` on a wrapping dialog (the popup only). */
  titleId?: string;
}

/**
 * "Visitor at {location} asking for {askingFor}" plus the answer buttons —
 * the ringing presentation shared by the full-screen popup
 * (`IncomingCallBanner`) and the call page (`/doorbell/call/:id`), so a
 * member sees the same design and buttons whichever one they land on.
 */
export function IncomingCallCard({ askingFor, location, isTest, onAnswer, answerBusy = false, decline, titleId }: IncomingCallCardProps) {
  return (
    <Stack align="center" gap="lg" maw={420} w="100%" mx="auto" data-testid="incoming-call-card">
      <ThemeIcon size={72} radius="xl" color="green" variant="light">
        <PhoneIncoming size={36} />
      </ThemeIcon>
      {isTest && (
        <Badge color="grape" variant="light">
          Test call
        </Badge>
      )}
      <Title order={2} ta="center" id={titleId}>
        Visitor at {location || "reception"} asking for {askingFor}
      </Title>
      <Stack gap="sm" w="100%">
        <Button
          size="lg"
          color="green"
          leftSection={<Video size={18} />}
          loading={answerBusy}
          disabled={answerBusy}
          onClick={() => onAnswer(true)}
        >
          Answer with video
        </Button>
        <Button
          size="lg"
          variant="light"
          color="green"
          leftSection={<Phone size={18} />}
          disabled={answerBusy}
          onClick={() => onAnswer(false)}
        >
          Answer with voice only
        </Button>
        {decline && (
          <Button
            size="lg"
            variant="light"
            color="red"
            leftSection={<PhoneOff size={18} />}
            loading={decline.loading}
            onClick={decline.onDecline}
          >
            Decline
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
